import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  apiClient,
  persistUserBearerToken,
  setLogoutHandler,
  setUserSessionToken,
} from "../services/apiClientService";
import { identifyCustomer, resetAnalytics } from "../analytics/posthog";

export const UserAuthContext = createContext();

export const UserAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = (userInfo, sessionToken = null) => {
    if (sessionToken) setUserSessionToken(sessionToken);
    setIsAuthenticated(true);
    setUser(userInfo);
    localStorage.setItem("user", JSON.stringify(userInfo));
    localStorage.setItem("user_isAuthenticated", "true");
    identifyCustomer(userInfo);
  };

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await apiClient("POST", "/api/users/logout");
    } catch (err) {
      console.error("User logout failed:", err);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem("user");
      localStorage.removeItem("user_isAuthenticated");
      persistUserBearerToken();
      resetAnalytics();
      setLoading(false);
    }
  }, []);

  const handleUnauthorized = useCallback(async () => {
    await logout();
  }, [logout]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const profile = await apiClient("GET", "/api/users/profile", null, {
          skipLoading: true,
        });
        if (cancelled) return;
        if (profile?.email) {
          setUser(profile);
          setIsAuthenticated(true);
          localStorage.setItem("user", JSON.stringify(profile));
          localStorage.setItem("user_isAuthenticated", "true");
          identifyCustomer(profile);
        } else {
          setIsAuthenticated(false);
          setUser(null);
          localStorage.removeItem("user");
          localStorage.removeItem("user_isAuthenticated");
        }
      } catch {
        if (cancelled) return;
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem("user");
        localStorage.removeItem("user_isAuthenticated");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    setLogoutHandler("user", handleUnauthorized);
    return () => {
      cancelled = true;
    };
  }, [handleUnauthorized]);

  return (
    <UserAuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        user,
        setUser,
        loading,
      }}
    >
      {children}
    </UserAuthContext.Provider>
  );
};

export const useUserAuth = () => useContext(UserAuthContext);
